import type { FieldPreset } from '@components/RingilDataTable/v3/columns/columnModel';

/**
 * Keys, sidebar groups and presets of the expedition list's v3 field catalogue — plain data, kept
 * apart from the renderers in expeditionFields.tsx so their consistency is unit-tested.
 */

/** Persisted in tabs, so these strings never change; a field that goes away is dropped on load. */
export const ExpeditionField = {
  ApplicationId: 'APPLICATION_ID',
  ShipmentName: 'SHIPMENT_NAME',
  SupplierManifest: 'SUPPLIER_MANIFEST',
  ResponsiblePerson: 'RESPONSIBLE_PERSON',
  SupplierNotification: 'SUPPLIER_NOTIFICATION',
  Labels: 'LABELS',
  AiClassification: 'AI_CLASSIFICATION',
  Status: 'STATUS',
  ManualMode: 'MANUAL_MODE',
  LoadingLocation: 'LOADING_LOCATION',
  LoadingDate: 'LOADING_DATE',
  LoadingTime: 'LOADING_TIME',
  LoadingTimezone: 'LOADING_TIMEZONE',
  UnloadingLocation: 'UNLOADING_LOCATION',
  UnloadingDate: 'UNLOADING_DATE',
  UnloadingTime: 'UNLOADING_TIME',
  UnloadingTimezone: 'UNLOADING_TIMEZONE',
  TotalPrice: 'TOTAL_PRICE',
  BasePrice: 'BASE_PRICE',
  AssignedCarrier: 'ASSIGNED_CARRIER',
  CarInfo: 'CAR_INFO',
  TenderStatus: 'TENDER_STATUS',
  InfoExpandAll: 'INFO_EXPAND_ALL',
  Favorite: 'FAVORITE',
  ExpeditionType: 'EXPEDITION_TYPE',
  CreatedBy: 'CREATED_BY',
  Orders: 'ORDERS',
  Note: 'NOTE',
  ExtraCosts: 'EXTRA_COSTS',
  Stops: 'STOPS',
  Invoiced: 'INVOICED',
  CustomsStatus: 'CUSTOMS_STATUS',
  Attachments: 'ATTACHMENTS',
  TransportLegs: 'TRANSPORT_LEGS',
  PairingSymbol: 'PAIRING_SYMBOL',
  TrackingNumber: 'TRACKING_NUMBER',
  TransportationType: 'TRANSPORTATION_TYPE',
  CreatedAt: 'CREATED_AT',
  Folder: 'FOLDER',
  CustomsLoadingNumber: 'CUSTOMS_LOADING_NUMBER',
  LadenFactor: 'LADEN_FACTOR',
  Roundtrip: 'ROUNDTRIP',
  PartialDelivery: 'PARTIAL_DELIVERY',
  AiDispatcherChange: 'AI_DISPATCHER_CHANGE',
} as const;

const F = ExpeditionField;

/** Sidebar sections — the cells the fields come from. */
export const ExpeditionFieldGroup = {
  Shipment: 'AllExpedition.expediceHeaderTitle',
  State: 'AllExpedition.stateHeaderTitle',
  Route: 'AllExpedition.route',
  Tender: 'AllExpedition.tenderColumnHeader',
  AdditionalInfo: 'AllExpedition.additionalInfo',
} as const;

/** Fallback texts of the group keys, for the sidebar section headings. */
export const EXPEDITION_FIELD_GROUPS: { key: string; fallback: string }[] = [
  { key: ExpeditionFieldGroup.Shipment, fallback: 'Expedice' },
  { key: ExpeditionFieldGroup.State, fallback: 'Stav' },
  { key: ExpeditionFieldGroup.Route, fallback: 'Trasa' },
  { key: ExpeditionFieldGroup.Tender, fallback: 'Tendr' },
  { key: ExpeditionFieldGroup.AdditionalInfo, fallback: 'Další informace' },
];

/**
 * Today's six cells as stacks of fields, plus the per-stop stacks from the Figma column sidebar. Widths
 * follow the Ringil 2.0 table (Figma 121:7401); the row checkbox adds its own room on top.
 */
export const EXPEDITION_PRESETS: FieldPreset[] = [
  {
    key: 'SHIPMENT',
    width: 220,
    header: 'AllExpedition.expediceHeaderTitle',
    headerDefault: 'Expedice',
    group: ExpeditionFieldGroup.Shipment,
    members: [F.ApplicationId, F.ShipmentName, F.SupplierManifest, F.ResponsiblePerson, F.SupplierNotification, F.Labels, F.AiClassification],
  },
  { key: 'STATE', width: 130, header: 'AllExpedition.stateHeaderTitle', headerDefault: 'Stav', group: ExpeditionFieldGroup.State, members: [F.Status, F.ManualMode] },
  {
    key: 'ROUTE',
    width: 200,
    header: 'AllExpedition.route',
    headerDefault: 'Trasa',
    group: ExpeditionFieldGroup.Route,
    members: [F.LoadingLocation, F.LoadingDate, F.LoadingTimezone, F.UnloadingLocation, F.UnloadingDate, F.UnloadingTimezone],
  },
  {
    key: 'LOADING',
    width: 180,
    header: 'generic.loadingPlaceDateTime',
    headerDefault: 'Loading – place, date, time',
    group: ExpeditionFieldGroup.Route,
    members: [F.LoadingLocation, F.LoadingDate, F.LoadingTime],
  },
  {
    key: 'UNLOADING',
    width: 180,
    header: 'generic.unloadingPlaceDateTime',
    headerDefault: 'Unloading – place, date, time',
    group: ExpeditionFieldGroup.Route,
    members: [F.UnloadingLocation, F.UnloadingDate, F.UnloadingTime],
  },
  {
    key: 'TENDER',
    width: 200,
    header: 'AllExpedition.tenderColumnHeader',
    headerDefault: 'Tendr',
    group: ExpeditionFieldGroup.Tender,
    members: [F.TotalPrice, F.AssignedCarrier, F.CarInfo, F.TenderStatus],
  },
  {
    key: 'ADDITIONAL_INFO',
    width: 300,
    header: 'AllExpedition.additionalInfo',
    headerDefault: 'Additional info',
    group: ExpeditionFieldGroup.AdditionalInfo,
    members: [
      F.InfoExpandAll,
      F.Favorite,
      F.ExpeditionType,
      F.CreatedBy,
      F.Orders,
      F.Note,
      F.ExtraCosts,
      F.Stops,
      F.Invoiced,
      F.CustomsStatus,
      F.Attachments,
      F.TransportLegs,
      F.PairingSymbol,
      F.TrackingNumber,
      F.TransportationType,
      F.AiDispatcherChange,
    ],
  },
];

/** The "Vše" tab: today's list, cell for cell. */
export const EXPEDITION_DEFAULT_COLUMNS: string[][] = ['SHIPMENT', 'STATE', 'ROUTE', 'TENDER', 'ADDITIONAL_INFO'].map(
  key => EXPEDITION_PRESETS.find(preset => preset.key === key).members,
);

export type ExpeditionFieldKey = (typeof ExpeditionField)[keyof typeof ExpeditionField];

/**
 * Whether the additional-info tags of a column show collapsed, each opened by a click: only beside
 * "Expand all", which is what opens them all. Anywhere else they show in full.
 */
export const infoTagsCollapsible = (columnMembers: string[]): boolean => columnMembers.includes(ExpeditionField.InfoExpandAll);
